// Generated from products/analytics/sdk/client.ts; run analytics generate:site-runtime.
import {
  AnalyticsEventSchema,
  BootstrapResponseSchema,
  BrowserRequestSchema,
  BrowserSignalsSchema,
  CollectResponseSchema,
  DevToolsSignalSchema,
  ErrorReportSchema,
  ReleaseVersionSchema,
  ErrorResponseSchema,
  IdentityPatchSchema,
  PageAttributionSchema,
  TrackInputSchema,
  VisitorIdSchema,
  SessionIdSchema,
  analyticsEventId,
} from "./contracts.gen";
import {
  CollectionStorageSchema,
  collectionPath,
  collectionStorageKey,
  collectionStorageLock,
} from "./collectionConfig.gen";
import {
  BINARY_RESPONSE_MAX_BYTES,
  decryptBinaryResponse,
  decryptResponse,
  encryptBinaryRequest,
  encryptRequest,
} from "./collectionCrypto.gen";
import { BrowserAnalyticsConfigSchema } from "./domainConfig.gen";
import type { BrowserAnalyticsConfig } from "./domainConfig.gen";
export type { BrowserAnalyticsConfig } from "./domainConfig.gen";
import { JSONR } from "./jsonr.gen";
import { EngagementSessionSchema } from "./engagement.gen";
import type { EngagementSnapshot } from "./engagement.gen";
import { browserEngagementEnvironment, observeEngagement } from "./pageEngagement.gen";
import type { EngagementEnvironment, EngagementObserver } from "./pageEngagement.gen";
import { readPageAttribution } from "./attribution.gen";
import { readBrowserSignals } from "./browserSignals.gen";
import { observeDevToolsSignal } from "./devToolsSignal.gen";
import { observePageErrors } from "./pageErrors.gen";
import type { BrowserSignals as ProbeBrowserSignals } from "./browserSignals.gen";
import type {
  AnalyticsEvent,
  BrowserContext,
  BrowserSignals,
  DevToolsSignal,
  IdentityPatch,
  PageAttribution,
  TrackInput,
  Attribution,
  SiteActionPayload,
  ErrorReport,
  AliveByMistakeContext,
  AliveByMistakeAccountContext,
  AliveByMistakeChoiceContext,
  AliveByMistakeActionPayload,
  AliveByMistakeSummaryPayload,
  AliveByMistakeReportQuery,
  AliveByMistakeReport,
} from "./contracts.gen";
import type { CollectionStorage } from "./collectionConfig.gen";

export { analyticsEventId };
export type {
  AnalyticsEvent,
  BrowserContext,
  BrowserSignals,
  DevToolsSignal,
  IdentityPatch,
  PageAttribution,
  TrackInput,
  Attribution,
  SiteActionPayload,
  AliveByMistakeContext,
  AliveByMistakeAccountContext,
  AliveByMistakeChoiceContext,
  AliveByMistakeActionPayload,
  AliveByMistakeSummaryPayload,
  AliveByMistakeReportQuery,
  AliveByMistakeReport,
};

const MAX_QUEUE = 100;
const SDK_VERSION = "0.2.0";
const MAX_BATCH = 25;
const MAX_ATTEMPTS = 3;
const MAX_DEVTOOLS_EVENTS = 10;
// Reserve Worker metadata headroom before the collector 64 KiB plaintext bound.
const PLAINTEXT_BUDGET_BYTES = 60 * 1024;
const KEEPALIVE_PLAINTEXT_BYTES = 40 * 1024;
const KEEPALIVE_BODY_BYTES = 64 * 1024;
const MAX_RESPONSE_BYTES = 96 * 1024;
const MAX_AGE_MS = 60_000;
const IDLE_SESSION_MS = 30 * 60_000;
const MAX_SEQUENCE = 2_147_483_647;
const MAX_PATH = 2048;
const MAX_REFERRER = 2048;
const MAX_TITLE = 300;
const MAX_LANGUAGE = 80;
const MAX_TIME_ZONE = 100;
const encoder = new TextEncoder();
const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const scopePattern = /^[A-Za-z0-9:._-]{1,512}$/;

export type Dimensions = BrowserContext["viewport"];

export type AnalyticsErrorCode =
  | "unsupported"
  | "storage_unavailable"
  | "lock_unavailable"
  | "lock_timeout"
  | "crypto_unavailable"
  | "bootstrap_failed"
  | "server_rejected"
  | "network"
  | "timeout"
  | "invalid_response"
  | "invalid_input"
  | "invalid_event"
  | "queue_overflow"
  | "event_expired"
  | "send_failed"
  | "internal_error";

export interface AnalyticsStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export interface AnalyticsLockManager {
  claim?(name: string, signal: AbortSignal): Promise<(() => void) | null>;
  request<T>(
    name: string,
    options: { mode: "exclusive"; signal?: AbortSignal },
    callback: () => Promise<T>,
  ): Promise<T>;
}

/** Narrow browser surface used by the SDK. Tests and embedders may replace it. */
export interface AnalyticsEnvironment {
  fetch: typeof fetch | null;
  storage: AnalyticsStorage | null;
  locks: AnalyticsLockManager | null;
  randomUUID: (() => string) | null;
  now: () => number;
  sessionStorage?: AnalyticsStorage | null | undefined;
  engagement?: EngagementEnvironment | undefined;
  hostname: () => string;
  locationPathname: () => string;
  locationSearch?: (() => string) | undefined;
  referrer: () => string;
  title: () => string;
  language: () => string;
  timeZone: () => string;
  viewport: () => Dimensions;
  screen: () => Dimensions;
  browserSignals?: (() => ProbeBrowserSignals) | undefined;
  devToolsObserver?: ((onSuspected: (signal: DevToolsSignal) => void) => () => void) | undefined;
  pageErrorObserver?: ((onError: (report: ErrorReport) => void) => () => void) | undefined;
  addPagehideListener?: ((listener: () => void) => void) | undefined;
  removePagehideListener?: ((listener: () => void) => void) | undefined;
  supportsKeepalive?: (() => boolean) | undefined;
}

export interface AnalyticsClient {
  track(input: TrackInput): void;
  identify(patch: IdentityPatch): void;
  flush(): Promise<void>;
  close(): void;
}

export interface AnalyticsClientOptions {
  /** Opt into the per-domain binary transport; omission preserves legacy clients. */
  config?: BrowserAnalyticsConfig | undefined;
  /** Include automatic browser signals, URL UTMs and heuristic DevTools events. */
  extendedTelemetry?: boolean | undefined;
  /** Public release label. Sent only after the receiver negotiates support. */
  releaseVersion?: string | undefined;
  /** Override automatic error capture independently of extended device telemetry. */
  captureErrors?: boolean | undefined;
  /** Passive visible-page timing. Requires explicit receiver support; default false. */
  captureEngagement?: boolean | undefined;
  /** Rechecked before capture and transport; denied collection drops pending events. */
  collectionAllowed?: (() => boolean) | undefined;
  environment?: AnalyticsEnvironment | undefined;
  onError?: ((code: AnalyticsErrorCode) => void) | undefined;
  lockWaitMs?: number | undefined;
  fetchTimeoutMs?: number | undefined;
  retryDelaysMs?: readonly number[] | undefined;
  flushDebounceMs?: number | undefined;
  flushTimeoutMs?: number | undefined;
  maxAgeMs?: number | undefined;
  idleSessionMs?: number | undefined;
}

type Status = "starting" | "ready" | "disabled" | "closed";
type CaptureInput =
  | TrackInput
  | { type: typeof analyticsEventId.identityUpdated; payload: IdentityPatch }
  | { type: typeof analyticsEventId.devToolsSuspected; payload: DevToolsSignal }
  | { type: typeof analyticsEventId.errorReported; payload: ErrorReport }
  | { type: typeof analyticsEventId.pageLifecycle; payload: EngagementSnapshot };
type PostOutcome =
  | { ok: true; status: number; value: unknown }
  | { ok: false; status: number; error: unknown };

class RequestFailure extends Error {
  constructor(readonly code: "timeout" | "network" | "invalid_response") {
    super(code);
    this.name = "RequestFailure";
  }
}
class StorageFailure extends Error {
  constructor() {
    super("storage_unavailable");
    this.name = "StorageFailure";
  }
}
class CryptoFailure extends Error {
  constructor() {
    super("crypto_unavailable");
    this.name = "CryptoFailure";
  }
}
class LockFailure extends Error {
  constructor(readonly code: "lock_unavailable" | "lock_timeout") {
    super(code);
    this.name = "LockFailure";
  }
}
class SizeFailure extends Error {
  constructor() {
    super("invalid_event");
    this.name = "SizeFailure";
  }
}

function positive(value: number | undefined, fallback: number): number {
  return typeof value === "number" && Number.isFinite(value) && value > 0
    ? Math.max(1, Math.floor(value))
    : fallback;
}

function nonNegative(value: number | undefined, fallback: number): number {
  return typeof value === "number" && Number.isFinite(value) && value >= 0
    ? Math.max(0, Math.floor(value))
    : fallback;
}

function readString(read: () => string): string {
  try {
    const value = read();
    return typeof value === "string" ? value : "";
  } catch {
    return "";
  }
}

function dimensions(read: () => Dimensions): Dimensions {
  try {
    const value = read();
    return { width: clampDimension(value?.width), height: clampDimension(value?.height) };
  } catch {
    return { width: 0, height: 0 };
  }
}

function clampDimension(value: unknown): number {
  if (typeof value !== "number" || !Number.isFinite(value)) return 0;
  return Math.min(32768, Math.max(0, Math.trunc(value)));
}

function sanitizePath(raw: string): string {
  let value = raw;
  const query = value.indexOf("?");
  if (query >= 0) value = value.slice(0, query);
  const fragment = value.indexOf("#");
  if (fragment >= 0) value = value.slice(0, fragment);
  if (!value.startsWith("/")) value = `/${value.replace(/^\/+/, "")}`;
  if (value.length === 0) value = "/";
  return value.slice(0, MAX_PATH);
}

function sanitizeReferrer(raw: string): string | null {
  if (raw.length === 0) return null;
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return null;
  }
  if (url.protocol !== "https:" && url.protocol !== "http:") return null;
  url.username = "";
  url.password = "";
  url.search = "";
  url.hash = "";
  return url.href.length > 0 && url.href.length <= MAX_REFERRER ? url.href : null;
}

function defaultStorage(): AnalyticsStorage | null {
  try {
    const storage = globalThis.localStorage;
    if (!storage) return null;
    return {
      getItem: (key) => storage.getItem(key),
      setItem: (key, value) => storage.setItem(key, value),
    };
  } catch {
    return null;
  }
}

function defaultLocks(): AnalyticsLockManager | null {
  try {
    if (typeof navigator === "undefined" || !navigator.locks) return null;
    const locks = navigator.locks;
    return {
      claim(name, signal) {
        return new Promise((resolve) => {
          if (signal.aborted) {
            resolve(null);
            return;
          }
          void locks
            .request(name, { mode: "exclusive", ifAvailable: true }, (lock) => {
              if (!lock || signal.aborted) {
                resolve(null);
                return;
              }
              return new Promise<void>((release) => {
                resolve(release);
              });
            })
            .catch(() => resolve(null));
        });
      },
      request<T>(
        name: string,
        options: { mode: "exclusive"; signal?: AbortSignal },
        callback: () => Promise<T>,
      ): Promise<T> {
        const lockOptions: LockOptions = { mode: options.mode };
        if (options.signal) lockOptions.signal = options.signal;
        return new Promise<T>((resolve, reject) => {
          void locks.request(name, lockOptions, () => callback()).then(resolve, reject);
        });
      },
    };
  } catch {
    return null;
  }
}

export function createBrowserEnvironment(): AnalyticsEnvironment {
  const hasWindow = typeof window !== "undefined" && typeof document !== "undefined";
  const environment: AnalyticsEnvironment = {
    fetch: typeof fetch === "function" ? fetch.bind(globalThis) : null,
    storage: defaultStorage(),
    locks: defaultLocks(),
    randomUUID:
      typeof globalThis.crypto?.randomUUID === "function"
        ? () => globalThis.crypto.randomUUID()
        : null,
    now: () => Date.now(),
    sessionStorage: (() => {
      try {
        return globalThis.sessionStorage ?? null;
      } catch {
        return null;
      }
    })(),
    engagement: browserEngagementEnvironment(),
    hostname: () => (hasWindow ? window.location.hostname : ""),
    locationPathname: () => (hasWindow ? window.location.pathname : "/"),
    locationSearch: () => (hasWindow ? window.location.search : ""),
    referrer: () => (hasWindow ? document.referrer : ""),
    title: () => (hasWindow ? document.title : ""),
    language: () => (hasWindow && typeof navigator !== "undefined" ? navigator.language : ""),
    timeZone: () => {
      try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone ?? "";
      } catch {
        return "";
      }
    },
    viewport: () =>
      hasWindow
        ? { width: window.innerWidth, height: window.innerHeight }
        : { width: 0, height: 0 },
    screen: () =>
      hasWindow && window.screen
        ? { width: window.screen.width, height: window.screen.height }
        : { width: 0, height: 0 },
    browserSignals: () => readBrowserSignals(),
    supportsKeepalive: () => typeof Request !== "undefined" && "keepalive" in Request.prototype,
  };
  if (hasWindow) {
    environment.devToolsObserver = (onSuspected) => observeDevToolsSignal(onSuspected);
    environment.pageErrorObserver = (onError) => observePageErrors(onError);
  }
  if (hasWindow && typeof window.addEventListener === "function") {
    environment.addPagehideListener = (listener) => window.addEventListener("pagehide", listener);
    environment.removePagehideListener = (listener) =>
      window.removeEventListener("pagehide", listener);
  }
  return environment;
}

export function createAnalyticsClient(options: AnalyticsClientOptions = {}): AnalyticsClient {
  const environment = options.environment ?? createBrowserEnvironment();
  const onError = options.onError;
  const extendedTelemetry = options.extendedTelemetry !== false;
  const captureErrors = options.captureErrors ?? extendedTelemetry;
  const parsedRelease = ReleaseVersionSchema.safeParse(options.releaseVersion);
  const releaseVersion = parsedRelease.success ? parsedRelease.data : undefined;
  let errorEventsSupported = false;
  let releaseMetadataSupported = false;
  let engagementSupported = false;
  const captureEngagement = options.captureEngagement === true;
  let engagementObserver: EngagementObserver | null = null;
  let sessionStorageReady = false;
  let sessionClaimRelease: (() => void) | null = null;
  let sessionClaimId = "";
  let sessionClaimGeneration = 0;
  let sessionClaimPending = false;
  let sessionOwnershipSuspended = false;
  let sessionPersistenceUnavailable = false;
  let sessionStorageKey = "";
  const reservedIds = new Set<string>();
  const lockWaitMs = positive(options.lockWaitMs, 5_000);
  const fetchTimeoutMs = positive(options.fetchTimeoutMs, 10_000);
  const retryDelays = options.retryDelaysMs ?? [500, 1_000];
  const flushDebounceMs = nonNegative(options.flushDebounceMs, 100);
  const flushTimeoutMs = positive(options.flushTimeoutMs, 15_000);
  const maxAgeMs = positive(options.maxAgeMs, MAX_AGE_MS);
  const idleSessionMs = positive(options.idleSessionMs, IDLE_SESSION_MS);

  let status: Status = "starting";
  let closed = false;
  let visitorId: string | null = null;
  let handle: string | null = null;
  let queue: AnalyticsEvent[] = [];
  let departureSnapshot: AnalyticsEvent | null = null;
  const attemptsById = new Map<string, number>();
  let sessionId = "";
  let sequence = 0;
  let lastActivity = 0;
  let pumping = false;
  let pagehideStarted = false;
  let pendingFinalSend: "hidden" | "pagehide" | null = null;
  let pumpTimer: ReturnType<typeof setTimeout> | null = null;
  let expiryTimer: ReturnType<typeof setTimeout> | null = null;
  let idleTimer: ReturnType<typeof setTimeout> | null = null;
  let pagehideListener: (() => void) | null = null;
  let devToolsCleanup: (() => void) | null = null;
  let errorCleanup: (() => void) | null = null;
  let errorEvents = 0;
  let devToolsEvents = 0;
  let signalsRead = false;
  let browserSignals: BrowserSignals | null = null;
  const activeRequests = new Set<AbortController>();
  const activeLocks = new Set<AbortController>();
  const pendingDelays = new Set<{
    timer: ReturnType<typeof setTimeout>;
    resolve: () => void;
  }>();
  let flushWaiters: Array<{ resolve: () => void; timer: ReturnType<typeof setTimeout> }> = [];
  const reported = new Set<AnalyticsErrorCode>();
  let scope = "";
  let browserConfig: BrowserAnalyticsConfig | undefined;

  function now(): number {
    try {
      const value = environment.now();
      return Number.isFinite(value) ? value : Date.now();
    } catch {
      return Date.now();
    }
  }

  function active(): boolean {
    if (!closed && options.collectionAllowed) {
      try {
        if (!options.collectionAllowed()) closeClient();
      } catch {
        closeClient();
      }
    }
    return !closed && (status === "starting" || status === "ready");
  }

  function report(code: AnalyticsErrorCode): void {
    if (reported.has(code)) return;
    reported.add(code);
    try {
      onError?.(code);
    } catch {
      // Diagnostics are best effort and never affect the caller.
    }
  }

  function generateUuid(): string | null {
    try {
      const value = environment.randomUUID?.();
      return typeof value === "string" && uuidPattern.test(value) ? value : null;
    } catch {
      return null;
    }
  }

  function clearTimers(): void {
    if (pumpTimer !== null) {
      clearTimeout(pumpTimer);
      pumpTimer = null;
    }
    if (expiryTimer !== null) {
      clearTimeout(expiryTimer);
      expiryTimer = null;
    }
    if (idleTimer !== null) {
      clearTimeout(idleTimer);
      idleTimer = null;
    }
    for (const pending of pendingDelays) {
      clearTimeout(pending.timer);
      pending.resolve();
    }
    pendingDelays.clear();
  }

  function abortRequests(): void {
    for (const controller of activeRequests) controller.abort();
    for (const controller of activeLocks) controller.abort();
    activeRequests.clear();
    activeLocks.clear();
  }

  function removePagehideListener(): void {
    if (pagehideListener && environment.removePagehideListener) {
      try {
        environment.removePagehideListener(pagehideListener);
      } catch {
        // The listener is already detached or unavailable.
      }
    }
    pagehideListener = null;
  }

  function stopDevToolsObserver(): void {
    const cleanup = devToolsCleanup;
    devToolsCleanup = null;
    if (!cleanup) return;
    try {
      cleanup();
    } catch {
      // The observer is best effort and must not affect close or disable.
    }
  }

  function stopErrorObserver(): void {
    const cleanup = errorCleanup;
    errorCleanup = null;
    if (cleanup) {
      try {
        cleanup();
      } catch {
        /* Observation must not prevent close. */
      }
    }
  }

  function resolveFlushWaiters(): void {
    const waiters = flushWaiters;
    flushWaiters = [];
    for (const waiter of waiters) {
      clearTimeout(waiter.timer);
      waiter.resolve();
    }
  }

  function disable(code: AnalyticsErrorCode): void {
    if (status === "closed" || status === "disabled") return;
    status = "disabled";
    report(code);
    clearTimers();
    removePagehideListener();
    stopDevToolsObserver();
    stopErrorObserver();
    stopEngagementObserver();
    releaseSessionOwnership(false);
    abortRequests();
    queue = [];
    departureSnapshot = null;
    attemptsById.clear();
    handle = null;
    resolveFlushWaiters();
  }

  function closeClient(): void {
    if (closed) return;
    closed = true;
    status = "closed";
    clearTimers();
    removePagehideListener();
    stopDevToolsObserver();
    stopErrorObserver();
    stopEngagementObserver();
    releaseSessionOwnership(false);
    abortRequests();
    queue = [];
    departureSnapshot = null;
    attemptsById.clear();
    handle = null;
    resolveFlushWaiters();
  }

  function delay(ms: number): Promise<void> {
    return new Promise((resolve) => {
      const pending = {
        timer: setTimeout(() => {
          pendingDelays.delete(pending);
          resolve();
        }, ms),
        resolve,
      };
      pendingDelays.add(pending);
    });
  }

  function isExpired(event: AnalyticsEvent): boolean {
    return now() - event.occurredAt.getTime() >= maxAgeMs;
  }

  function expireEvents(): void {
    const ids = new Set(queue.filter(isExpired).map((event) => event.id));
    if (ids.size) {
      removeEvents(ids);
      report("event_expired");
    }
  }

  function scheduleExpiry(): void {
    if (expiryTimer !== null) {
      clearTimeout(expiryTimer);
      expiryTimer = null;
    }
    if (closed || status === "disabled" || queue.length === 0) return;
    const oldest = queue[0];
    if (!oldest) return;
    const remaining = oldest.occurredAt.getTime() + maxAgeMs - now();
    expiryTimer = setTimeout(
      () => {
        expiryTimer = null;
        if (closed || status === "disabled" || pumping || pagehideStarted) return;
        expireEvents();
        scheduleExpiry();
        if (status === "ready" && queue.length > 0 && !pumping && pumpTimer === null) {
          schedulePump(0);
        }
      },
      Math.max(0, remaining),
    );
  }

  function stopEngagementObserver(): void {
    const observer = engagementObserver;
    engagementObserver = null;
    try {
      observer?.close();
    } catch {
      /* Passive observation is best effort. */
    }
  }

  function persistSession(): void {
    if (
      !captureEngagement ||
      !sessionStorageReady ||
      !visitorId ||
      !sessionClaimRelease ||
      sessionClaimId !== sessionId
    )
      return;
    try {
      const record = EngagementSessionSchema.parse({
        version: 1,
        visitorId,
        sessionId,
        nextSequence: sequence,
        lastSignalAt: Math.floor(lastActivity),
      });
      environment.sessionStorage?.setItem(sessionStorageKey, JSONR.stringify(record));
    } catch {
      /* Session storage is optional; keep this tab's in-memory session. */
    }
  }

  function releaseSessionOwnership(suspend: boolean): void {
    sessionClaimGeneration++;
    sessionClaimPending = false;
    const release = sessionClaimRelease;
    sessionClaimRelease = null;
    sessionClaimId = "";
    if (suspend && !sessionPersistenceUnavailable) sessionOwnershipSuspended = true;
    try {
      release?.();
    } catch {
      /* Ownership cleanup is best effort. */
    }
  }

  async function trySessionClaim(candidate: string): Promise<(() => void) | null> {
    const claim = environment.locks?.claim;
    if (!claim || !visitorId || !environment.sessionStorage) return null;
    const controller = new AbortController();
    activeLocks.add(controller);
    let timer: ReturnType<typeof setTimeout> | null = null;
    let onAbort: (() => void) | null = null;
    try {
      const cancellation = new Promise<null>((resolve) => {
        onAbort = () => resolve(null);
        controller.signal.addEventListener("abort", onAbort, { once: true });
        timer = setTimeout(() => controller.abort(), lockWaitMs);
      });
      const attempt = claim
        .call(
          environment.locks,
          `${sessionStorageKey}:${visitorId}:${candidate}`,
          controller.signal,
        )
        .then((release) => {
          if (controller.signal.aborted || !active()) {
            release?.();
            return null;
          }
          return release;
        })
        .catch(() => null);
      return await Promise.race([attempt, cancellation]);
    } catch {
      return null;
    } finally {
      if (timer !== null) clearTimeout(timer);
      if (onAbort) controller.signal.removeEventListener("abort", onAbort);
      activeLocks.delete(controller);
    }
  }

  function savedSession() {
    try {
      const raw = environment.sessionStorage?.getItem(sessionStorageKey);
      const saved =
        raw && raw.length <= 2048 ? EngagementSessionSchema.safeParse(JSONR.parse(raw)) : null;
      const at = now();
      return saved?.success &&
        saved.data.visitorId === visitorId &&
        saved.data.lastSignalAt <= at &&
        at - saved.data.lastSignalAt < idleSessionMs
        ? saved.data
        : null;
    } catch {
      return null;
    }
  }

  async function restoreSession(): Promise<void> {
    if (!captureEngagement || !visitorId || sessionStorageReady) return;
    sessionStorageKey = `__s1:${scope}:${browserConfig?.path ?? collectionPath}`;
    const saved = savedSession();
    const candidate =
      saved && saved.nextSequence + sequence < MAX_SEQUENCE ? saved.sessionId : sessionId;
    const generation = ++sessionClaimGeneration;
    let release = await trySessionClaim(candidate);
    let owned = candidate;
    if (!release && candidate !== sessionId) {
      owned = sessionId;
      release = await trySessionClaim(owned);
    }
    if (!active() || generation !== sessionClaimGeneration) {
      release?.();
      return;
    }
    if (release) {
      sessionClaimRelease = release;
      sessionClaimId = owned;
      if (saved && owned === saved.sessionId) {
        const previous = sessionId;
        sessionId = saved.sessionId;
        const offset = saved.nextSequence;
        queue = queue.map((event) =>
          event.sessionId === previous
            ? {
                ...event,
                sessionId: SessionIdSchema.parse(sessionId),
                sequence: event.sequence + offset,
              }
            : event,
        );
        sequence += offset;
      }
    } else sessionPersistenceUnavailable = true;
    sessionStorageReady = true;
    persistSession();
  }

  function claimCurrentSession(resume: boolean): void {
    if (
      !captureEngagement ||
      !sessionStorageReady ||
      sessionPersistenceUnavailable ||
      sessionClaimPending
    )
      return;
    sessionClaimPending = true;
    const candidate = sessionId,
      generation = ++sessionClaimGeneration;
    void trySessionClaim(candidate).then((release) => {
      if (!active() || generation !== sessionClaimGeneration || candidate !== sessionId) {
        release?.();
        return;
      }
      sessionClaimPending = false;
      if (release) {
        sessionClaimRelease = release;
        sessionClaimId = candidate;
        const saved = savedSession();
        if (saved?.sessionId === candidate) sequence = Math.max(sequence, saved.nextSequence);
        sessionOwnershipSuspended = false;
        persistSession();
      } else if (resume) {
        // A live cloned document owns the old session. This document gets a new
        // UUID; already queued events remain immutable in their original session.
        sessionOwnershipSuspended = false;
        rotateSession(now());
      } else sessionPersistenceUnavailable = true;
      try {
        if (environment.engagement?.visible()) engagementObserver?.resume();
      } catch {
        /* Best effort return. */
      }
    });
  }

  function prepareEngagement(): boolean {
    if (!active()) return false;
    if (sessionOwnershipSuspended) {
      claimCurrentSession(true);
      return false;
    }
    return ensureSession();
  }

  function ensureSession(): boolean {
    if (!active()) return false;
    const at = now();
    // Host calls never await a BFCache ownership claim or adopt cloned storage.
    if (sessionOwnershipSuspended && !rotateSession(at)) return false;
    return (at - lastActivity < idleSessionMs && sequence <= MAX_SEQUENCE) || rotateSession(at);
  }

  function rotateSession(at: number): boolean {
    const next = generateUuid();
    if (!next) {
      disable("crypto_unavailable");
      return false;
    }
    releaseSessionOwnership(false);
    sessionOwnershipSuspended = false;
    sessionId = next;
    sequence = 0;
    errorEvents = 0;
    lastActivity = at;
    engagementObserver?.reset();
    claimCurrentSession(false);
    persistSession();
    return true;
  }

  function scheduleIdleTimer(): void {
    if (idleTimer !== null) {
      clearTimeout(idleTimer);
      idleTimer = null;
    }
    if (!active() || captureEngagement) return;
    idleTimer = setTimeout(() => {
      idleTimer = null;
      if (!active()) return;
      rotateSession(now());
    }, idleSessionMs);
  }

  function readSignals(): BrowserSignals | null {
    if (!extendedTelemetry) return null;
    if (signalsRead) return browserSignals;
    signalsRead = true;
    if (!environment.browserSignals) return null;
    try {
      const parsed = BrowserSignalsSchema.safeParse(environment.browserSignals());
      browserSignals = parsed.success ? parsed.data : null;
    } catch {
      browserSignals = null;
    }
    return browserSignals;
  }

  function readAttribution(): PageAttribution | null {
    if (!extendedTelemetry) return null;
    try {
      const parsed = PageAttributionSchema.safeParse(
        readPageAttribution(readString(() => environment.locationSearch?.() ?? "")),
      );
      return parsed.success && Object.keys(parsed.data).length > 0 ? parsed.data : null;
    } catch {
      return null;
    }
  }

  function browserContext(): BrowserContext {
    const context: BrowserContext = {
      language: readString(environment.language).slice(0, MAX_LANGUAGE),
      timeZone: readString(environment.timeZone).slice(0, MAX_TIME_ZONE),
      viewport: dimensions(environment.viewport),
      screen: dimensions(environment.screen),
    };
    const signals = readSignals();
    return signals ? { ...context, signals } : context;
  }

  function capture(input: CaptureInput): void {
    if (!active()) return;
    if (!ensureSession()) return;
    const at = now();
    lastActivity = at;
    if (sequence > MAX_SEQUENCE && !rotateSession(at)) return;
    const id = generateUuid();
    if (!id) {
      disable("crypto_unavailable");
      return;
    }
    const base = {
      id,
      sessionId,
      sequence: sequence++,
      occurredAt: new Date(at),
      pagePath: sanitizePath(readString(environment.locationPathname)),
      referrer: sanitizeReferrer(readString(environment.referrer)),
      browserContext: browserContext(),
      ...(releaseMetadataSupported
        ? { sdkVersion: SDK_VERSION, ...(releaseVersion ? { releaseVersion } : {}) }
        : {}),
    };
    let event: unknown;
    switch (input.type) {
      case analyticsEventId.pageViewed: {
        const utm = readAttribution();
        event = {
          ...base,
          type: input.type,
          payload: { ...input.payload, ...(utm ? { utm } : {}) },
        };
        break;
      }
      case analyticsEventId.playClicked:
        event = { ...base, type: input.type, payload: {} };
        break;
      case analyticsEventId.gameEntered:
        event = { ...base, type: input.type, payload: {} };
        break;
      case analyticsEventId.identityUpdated:
      case analyticsEventId.siteAction:
        event = { ...base, type: input.type, payload: input.payload };
        break;
      case analyticsEventId.devToolsSuspected:
      case analyticsEventId.errorReported:
      case analyticsEventId.pageLifecycle:
        event = { ...base, type: input.type, payload: input.payload };
        break;
    }
    const parsed = AnalyticsEventSchema.safeParse(event);
    if (!parsed.success) {
      report("invalid_event");
      return;
    }
    persistSession();
    scheduleIdleTimer();
    // One bounded final snapshot is reserved separately when the host queue is
    // full. It never displaces a host action and uses the same25-event budget.
    if (
      parsed.data.type === analyticsEventId.pageLifecycle &&
      parsed.data.payload.reason === "pagehide"
    )
      departureSnapshot = parsed.data;
    if (parsed.data.type === analyticsEventId.pageLifecycle) {
      // Only snapshots with no transport attempt or encryption reservation may be coalesced.
      const incoming = parsed.data;
      queue = queue.filter(
        (queued) =>
          !(
            queued.type === analyticsEventId.pageLifecycle &&
            queued.sessionId === incoming.sessionId &&
            queued.payload.pageVisitId === incoming.payload.pageVisitId &&
            !reservedIds.has(queued.id) &&
            !attemptsById.has(queued.id)
          ),
      );
      // Reserve capacity for host actions; lifecycle capture never evicts them.
      if (queue.length >= MAX_QUEUE - 10) return;
    }
    if (parsed.data.type !== analyticsEventId.pageLifecycle && queue.length >= MAX_QUEUE) {
      const disposable = queue.find(
        (queued) =>
          queued.type === analyticsEventId.pageLifecycle &&
          !reservedIds.has(queued.id) &&
          !attemptsById.has(queued.id),
      );
      if (disposable) removeEvents(new Set([disposable.id]));
    }
    if (queue.length >= MAX_QUEUE) {
      report("queue_overflow");
      return;
    }
    queue.push(parsed.data);
    scheduleExpiry();
    if (status === "ready" && !pumping && pumpTimer === null) schedulePump(flushDebounceMs);
  }

  function normalizeTrack(input: TrackInput): TrackInput | null {
    try {
      if (input.type === analyticsEventId.pageViewed) {
        const parsed = TrackInputSchema.safeParse({
          type: analyticsEventId.pageViewed,
          payload: { ...input.payload, title: input.payload.title.slice(0, MAX_TITLE) },
        });
        return parsed.success ? parsed.data : null;
      }
      const parsed = TrackInputSchema.safeParse(input);
      return parsed.success ? parsed.data : null;
    } catch {
      return null;
    }
  }

  function track(input: TrackInput): void {
    if (!active()) return;
    const parsed = normalizeTrack(input);
    if (!parsed) {
      report("invalid_input");
      return;
    }
    capture(parsed);
  }

  function identify(patch: IdentityPatch): void {
    if (!active()) return;
    const parsed = IdentityPatchSchema.safeParse(patch);
    if (!parsed.success) {
      report("invalid_input");
      return;
    }
    const payload: IdentityPatch = {
      ...(parsed.data.name !== undefined ? { name: parsed.data.name } : {}),
      ...(parsed.data.email !== undefined ? { email: parsed.data.email } : {}),
      ...(parsed.data.externalUserId !== undefined
        ? { externalUserId: parsed.data.externalUserId }
        : {}),
      ...(parsed.data.externalAccountId !== undefined
        ? { externalAccountId: parsed.data.externalAccountId }
        : {}),
    };
    capture({ type: analyticsEventId.identityUpdated, payload });
  }

  function readStorage(): CollectionStorage | null {
    let raw: string | null;
    try {
      raw = environment.storage?.getItem(collectionStorageKey) ?? null;
    } catch {
      throw new StorageFailure();
    }
    if (raw === null) return null;
    let value: unknown;
    try {
      value = JSON.parse(raw);
    } catch {
      return null;
    }
    const parsed = CollectionStorageSchema.safeParse(value);
    if (parsed.success) return parsed.data;
    if (value && typeof value === "object" && "visitorId" in value) {
      const visitor = VisitorIdSchema.safeParse((value as { visitorId: unknown }).visitorId);
      if (visitor.success) return { visitorId: visitor.data };
    }
    return null;
  }

  function writeStorage(value: CollectionStorage): void {
    let serialized: string;
    try {
      serialized = JSON.stringify(CollectionStorageSchema.parse(value));
    } catch {
      throw new StorageFailure();
    }
    const storage = environment.storage;
    if (!storage) throw new StorageFailure();
    try {
      storage.setItem(collectionStorageKey, serialized);
    } catch {
      throw new StorageFailure();
    }
  }

  function loadOrCreateStorage(): CollectionStorage {
    const existing = readStorage();
    if (existing) {
      writeStorage(existing);
      return existing;
    }
    const id = generateUuid();
    if (!id) throw new CryptoFailure();
    const created = CollectionStorageSchema.parse({ visitorId: id });
    writeStorage(created);
    return created;
  }

  async function withStorageLock<T>(callback: () => Promise<T>): Promise<T> {
    const locks = environment.locks;
    if (!locks) throw new LockFailure("lock_unavailable");
    const controller = new AbortController();
    activeLocks.add(controller);
    let entered = false;
    let timer: ReturnType<typeof setTimeout> | null = setTimeout(
      () => controller.abort(),
      lockWaitMs,
    );
    try {
      return await locks.request(
        collectionStorageLock,
        { mode: "exclusive", signal: controller.signal },
        async () => {
          entered = true;
          if (timer !== null) {
            clearTimeout(timer);
            timer = null;
          }
          return await callback();
        },
      );
    } catch (error) {
      if (!entered) {
        throw new LockFailure(controller.signal.aborted ? "lock_timeout" : "lock_unavailable");
      }
      throw error;
    } finally {
      if (timer !== null) clearTimeout(timer);
      activeLocks.delete(controller);
    }
  }

  async function boundedResponseBytes(response: Response): Promise<Uint8Array<ArrayBuffer>> {
    const maxBytes = browserConfig ? BINARY_RESPONSE_MAX_BYTES : MAX_RESPONSE_BYTES;
    const length = response.headers.get("content-length");
    if (length !== null && (!/^\d+$/.test(length) || Number(length) > maxBytes))
      throw new RequestFailure("invalid_response");
    const reader = response.body?.getReader();
    if (!reader) return new Uint8Array();
    const chunks: Uint8Array[] = [];
    let total = 0;
    try {
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        total += value.byteLength;
        if (total > maxBytes) {
          await reader.cancel();
          throw new RequestFailure("invalid_response");
        }
        chunks.push(value);
      }
    } finally {
      reader.releaseLock();
    }
    const bytes = new Uint8Array(total);
    let offset = 0;
    for (const chunk of chunks) {
      bytes.set(chunk, offset);
      offset += chunk.byteLength;
    }
    return bytes;
  }

  async function requestRaw(
    body: string | Uint8Array<ArrayBuffer> | null,
    method: "GET" | "POST",
    keepalive = false,
  ): Promise<{ ok: boolean; status: number; bytes: Uint8Array<ArrayBuffer> }> {
    if (!active()) throw new RequestFailure("network");
    const fetchFn = environment.fetch;
    if (!fetchFn) throw new RequestFailure("network");
    const controller = new AbortController();
    activeRequests.add(controller);
    let timer: ReturnType<typeof setTimeout> | null = null;
    const timeout = new Promise<never>((_, reject) => {
      timer = setTimeout(() => {
        controller.abort();
        reject(new RequestFailure("timeout"));
      }, fetchTimeoutMs);
    });
    const init: RequestInit = {
      method,
      credentials: "omit",
      cache: "no-store",
      redirect: "error",
      referrerPolicy: "no-referrer",
      signal: controller.signal,
    };
    if (method === "POST") {
      init.headers = {
        "content-type": browserConfig ? "application/octet-stream" : "application/json",
      };
      init.body = body ?? "";
    } else {
      init.headers = { accept: "application/json" };
    }
    if (keepalive) init.keepalive = true;
    try {
      const response = await Promise.race([
        fetchFn(browserConfig?.path ?? collectionPath, init),
        timeout,
      ]);
      if (
        browserConfig &&
        response.ok &&
        response.headers.get("content-type")?.split(";")[0]?.trim() !== "application/octet-stream"
      ) {
        await response.body?.cancel();
        throw new RequestFailure("invalid_response");
      }
      const bytes = await Promise.race([boundedResponseBytes(response), timeout]);
      return { ok: response.ok, status: response.status, bytes };
    } catch (error) {
      if (error instanceof RequestFailure) throw error;
      if (controller.signal.aborted) throw new RequestFailure("timeout");
      throw new RequestFailure("network");
    } finally {
      if (timer !== null) clearTimeout(timer);
      activeRequests.delete(controller);
    }
  }

  async function prepareRequest(value: unknown): Promise<{
    body: string | Uint8Array<ArrayBuffer>;
    decrypt: (bytes: Uint8Array) => Promise<unknown>;
  }> {
    try {
      if (browserConfig) {
        const encrypted = await encryptBinaryRequest(value, browserConfig);
        return {
          body: encrypted.body,
          decrypt: (bytes) => decryptBinaryResponse(bytes, encrypted.context),
        };
      }
      const encrypted = await encryptRequest(value, scope);
      return {
        body: JSON.stringify(encrypted.envelope),
        decrypt: (bytes) =>
          decryptResponse(
            JSON.parse(new TextDecoder("utf-8", { fatal: true }).decode(bytes)),
            scope,
          ),
      };
    } catch {
      throw new CryptoFailure();
    }
  }

  async function postEncrypted(value: unknown, keepalive = false): Promise<PostOutcome> {
    const prepared = await prepareRequest(value);
    const response = await requestRaw(prepared.body, "POST", keepalive);
    try {
      const value = await prepared.decrypt(response.bytes);
      if (response.ok) return { ok: true, status: response.status, value };
      const parsed = ErrorResponseSchema.safeParse(value);
      return {
        ok: false,
        status: response.status,
        error: parsed.success ? parsed.data : undefined,
      };
    } catch {
      if (response.ok) throw new RequestFailure("invalid_response");
      return { ok: false, status: response.status, error: undefined };
    }
  }

  async function initializeStorage(): Promise<void> {
    const stored = await withStorageLock(async () => {
      if (!active()) throw new RequestFailure("network");
      return loadOrCreateStorage();
    });
    if (active()) {
      visitorId = stored.visitorId;
      await restoreSession();
    }
  }

  function failureCode(error: unknown): AnalyticsErrorCode {
    if (error instanceof RequestFailure) return error.code;
    if (error instanceof StorageFailure) return "storage_unavailable";
    if (error instanceof CryptoFailure) return "crypto_unavailable";
    if (error instanceof LockFailure) return error.code;
    if (error instanceof SizeFailure) return "invalid_event";
    return "internal_error";
  }

  async function initialize(): Promise<void> {
    for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
      if (!active()) return;
      try {
        await initializeStorage();
        if (!active()) return;
        scheduleIdleTimer();
        void bootstrapLoop().catch(() => {
          if (active()) disable("bootstrap_failed");
        });
        return;
      } catch (error) {
        if (!active()) return;
        report(failureCode(error));
        if (attempt + 1 >= MAX_ATTEMPTS) {
          disable(failureCode(error));
          return;
        }
        await delay(retryDelays[attempt] ?? 500);
      }
    }
  }

  function bindHandle(next: string): void {
    if (!active()) return;
    if (handle === null) handle = next;
    status = "ready";
    if (releaseMetadataSupported)
      queue = queue.map((event) => ({
        ...event,
        sdkVersion: SDK_VERSION,
        ...(releaseVersion ? { releaseVersion } : {}),
      }));
    startErrorObserver();
    startEngagementObserver();
    scheduleExpiry();
    if (!pumping) schedulePump(0);
  }

  async function bootstrapLoop(): Promise<void> {
    if (!visitorId) {
      disable("internal_error");
      return;
    }
    let negotiate: 2 | 1 | 0 = captureEngagement ? 2 : 1;
    for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
      if (!active()) return;
      try {
        const request = BrowserRequestSchema.parse({
          operation: "bootstrap",
          version: 1,
          visitorId,
          ...(negotiate ? { capabilitiesVersion: negotiate } : {}),
        });
        const outcome = await postEncrypted(request);
        if (!active()) return;
        if (outcome.ok) {
          const parsed = BootstrapResponseSchema.safeParse(outcome.value);
          if (parsed.success) {
            if (parsed.data.version >= 2 && "capabilities" in parsed.data) {
              errorEventsSupported = parsed.data.capabilities.errorEvents;
              releaseMetadataSupported = parsed.data.capabilities.releaseMetadata;
            }
            engagementSupported =
              negotiate === 2 && parsed.data.version === 3 && parsed.data.capabilities.engagement;
            bindHandle(parsed.data.handle);
            return;
          }
          report("invalid_response");
        } else {
          // Existing strict receivers reject the negotiation field. Keep their live
          // websites collecting legacy events until that receiver is upgraded.
          if (outcome.status === 400 && negotiate) {
            negotiate = negotiate === 2 ? 1 : 0;
            continue;
          }
          report("server_rejected");
          if (!isRetryableStatus(outcome.status)) {
            disable("bootstrap_failed");
            return;
          }
        }
      } catch (error) {
        if (!active()) return;
        report(failureCode(error));
        if (error instanceof CryptoFailure || error instanceof StorageFailure) {
          disable(failureCode(error));
          return;
        }
      }
      if (attempt + 1 < MAX_ATTEMPTS) await delay(retryDelays[attempt] ?? 500);
    }
    disable("bootstrap_failed");
  }

  function isRetryableStatus(statusCode: number): boolean {
    return statusCode >= 500 || statusCode === 408 || statusCode === 429;
  }

  function schedulePump(delayMs: number): void {
    if (closed || status !== "ready" || pumping || pagehideStarted) return;
    if (pumpTimer !== null) clearTimeout(pumpTimer);
    pumpTimer = setTimeout(
      () => {
        pumpTimer = null;
        void pump().catch(() => {
          pumping = false;
          resolveFlushWaiters();
          if (active()) disable("internal_error");
        });
      },
      Math.max(0, delayMs),
    );
  }

  function acceptsExact(value: unknown, sent: AnalyticsEvent[]): boolean {
    const parsed = CollectResponseSchema.safeParse(value);
    if (!parsed.success) return false;
    const accepted = parsed.data.acceptedIds;
    if (accepted.length !== sent.length) return false;
    const counts = new Map<string, number>();
    for (const id of accepted) counts.set(id, (counts.get(id) ?? 0) + 1);
    for (const event of sent) {
      const count = counts.get(event.id) ?? 0;
      if (count !== 1) return false;
      counts.delete(event.id);
    }
    return counts.size === 0;
  }

  function removeEvents(ids: ReadonlySet<string>): void {
    queue = queue.filter((event) => !ids.has(event.id));
    for (const id of ids) attemptsById.delete(id);
    if (departureSnapshot && ids.has(departureSnapshot.id)) departureSnapshot = null;
    scheduleExpiry();
  }

  function selectBatch(
    maxBytes: number,
    candidates: AnalyticsEvent[] = queue,
  ): { events: AnalyticsEvent[]; value: unknown } | null {
    if (handle === null) return null;
    const limit = Math.min(MAX_BATCH, candidates.length);
    for (let count = limit; count >= 1; count--) {
      const events = candidates.slice(0, count);
      const request = BrowserRequestSchema.safeParse({
        operation: "collect",
        version: 1,
        handle,
        events,
      });
      if (!request.success) continue;
      let text: string;
      try {
        text = JSONR.stringify(request.data);
      } catch {
        continue;
      }
      if (encoder.encode(text).byteLength <= maxBytes) return { events, value: request.data };
    }
    return null;
  }

  async function sendBatch(): Promise<"ok" | "failed"> {
    const batch = selectBatch(PLAINTEXT_BUDGET_BYTES);
    if (!batch) {
      const dropped = queue.shift();
      if (dropped) report("invalid_event");
      scheduleExpiry();
      return "ok";
    }
    const ids = new Set<string>(batch.events.map((event) => event.id));
    const stillQueued = () =>
      batch.events.every((event) => queue.some((queued) => queued.id === event.id));
    for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
      if (!active() || status !== "ready" || pagehideStarted) return "failed";
      if (!stillQueued()) return "ok";
      const expired = new Set(batch.events.filter(isExpired).map((event) => event.id));
      if (expired.size) {
        removeEvents(expired);
        report("event_expired");
        return "ok";
      }
      const exhausted = new Set(
        batch.events
          .filter((event) => (attemptsById.get(event.id) ?? 0) >= MAX_ATTEMPTS)
          .map((event) => event.id),
      );
      if (exhausted.size) {
        removeEvents(exhausted);
        report("send_failed");
        return "ok";
      }
      for (const event of batch.events)
        attemptsById.set(event.id, (attemptsById.get(event.id) ?? 0) + 1);
      try {
        const outcome = await postEncrypted(batch.value);
        if (!active()) return "failed";
        if (!stillQueued()) return "ok";
        if (outcome.ok) {
          if (acceptsExact(outcome.value, batch.events)) {
            removeEvents(ids);
            return "ok";
          }
          report("invalid_response");
        } else {
          report("server_rejected");
          if (!isRetryableStatus(outcome.status)) {
            removeEvents(ids);
            report("send_failed");
            return "failed";
          }
        }
      } catch (error) {
        if (!active()) return "failed";
        if (error instanceof CryptoFailure || error instanceof StorageFailure) {
          disable(failureCode(error));
          return "failed";
        }
        if (error instanceof SizeFailure) {
          removeEvents(ids);
          report("send_failed");
          return "failed";
        }
        report(failureCode(error));
      }
      if (attempt + 1 < MAX_ATTEMPTS) await delay(retryDelays[attempt] ?? 500);
    }
    removeEvents(ids);
    report("send_failed");
    return "failed";
  }

  async function pump(): Promise<void> {
    if (pumping || pagehideStarted || closed || status !== "ready") {
      if (!pumping && !pagehideStarted) resolveFlushWaiters();
      return;
    }
    pumping = true;
    if (pumpTimer !== null) {
      clearTimeout(pumpTimer);
      pumpTimer = null;
    }
    try {
      while (!closed && status === "ready" && !pagehideStarted) {
        expireEvents();
        if (queue.length === 0) break;
        const result = await sendBatch();
        if (closed || status !== "ready") break;
        if (result === "failed") break;
      }
    } finally {
      pumping = false;
      if (!pagehideStarted) resolveFlushWaiters();
      scheduleExpiry();
      if (!closed && status === "ready" && queue.length > 0) schedulePump(1_000);
    }
  }

  function requestFlush(): Promise<void> {
    if (!active()) return Promise.resolve();
    if (queue.length === 0 && !pumping && !pagehideStarted) return Promise.resolve();
    return new Promise<void>((resolve) => {
      const waiter = {
        resolve,
        timer: setTimeout(() => {
          flushWaiters = flushWaiters.filter((item) => item !== waiter);
          resolve();
        }, flushTimeoutMs),
      };
      flushWaiters.push(waiter);
      if (status === "ready" && !pumping) schedulePump(0);
    });
  }

  async function pagehideSend(reason: "hidden" | "pagehide"): Promise<void> {
    try {
      expireEvents();
      if (!active() || status !== "ready" || (queue.length === 0 && !departureSnapshot)) return;
      const eligible =
        reason === "hidden"
          ? queue.filter((event) => event.type === analyticsEventId.pageLifecycle)
          : queue;
      const exhausted = new Set(
        eligible
          .filter((event) => (attemptsById.get(event.id) ?? 0) >= MAX_ATTEMPTS)
          .map((event) => event.id),
      );
      if (exhausted.size) {
        removeEvents(exhausted);
        report("send_failed");
      }
      const lifecycle = queue
        .filter((event) => event.type === analyticsEventId.pageLifecycle)
        .reverse();
      if (
        reason === "pagehide" &&
        departureSnapshot &&
        departureSnapshot.type === analyticsEventId.pageLifecycle &&
        !isExpired(departureSnapshot) &&
        (attemptsById.get(departureSnapshot.id) ?? 0) < MAX_ATTEMPTS
      ) {
        const final = departureSnapshot;
        lifecycle.splice(
          0,
          lifecycle.length,
          final,
          ...lifecycle.filter((event) => event.id !== final.id),
        );
      }
      const candidates =
        reason === "hidden"
          ? lifecycle
          : [
              ...lifecycle,
              ...queue.filter((event) => event.type !== analyticsEventId.pageLifecycle),
            ];
      const batch = selectBatch(KEEPALIVE_PLAINTEXT_BYTES, candidates);
      if (!batch) return;
      for (const event of batch.events) reservedIds.add(event.id);
      const prepared = await prepareRequest(batch.value);
      const body = prepared.body;
      if (
        (typeof body === "string" ? encoder.encode(body).byteLength : body.byteLength) >
        KEEPALIVE_BODY_BYTES
      )
        return;
      // pagehideStarted blocks new normal attempts while encryption is pending.
      // Charge only queued events once the keepalive is ready to send.
      for (const event of batch.events) {
        if (queue.some((queued) => queued.id === event.id) || departureSnapshot?.id === event.id)
          attemptsById.set(event.id, (attemptsById.get(event.id) ?? 0) + 1);
      }
      const response = await requestRaw(body, "POST", true);
      if (!response.ok) return;
      const plaintext = await prepared.decrypt(response.bytes);
      if (acceptsExact(plaintext, batch.events)) {
        removeEvents(new Set<string>(batch.events.map((event) => event.id)));
      }
    } catch {
      // Pagehide delivery is best effort and may not finish before unload.
    } finally {
      reservedIds.clear();
    }
  }

  function startEngagementObserver(): void {
    if (!captureEngagement || !engagementSupported || engagementObserver || !environment.engagement)
      return;
    try {
      const observer = observeEngagement(
        environment.engagement,
        generateUuid,
        prepareEngagement,
        (payload) => {
          try {
            if (!active()) return;
            capture({ type: analyticsEventId.pageLifecycle, payload });
            if (payload.reason === "hidden" || payload.reason === "pagehide")
              onPagehide(payload.reason);
            if (payload.reason === "pagehide") releaseSessionOwnership(true);
          } catch {
            /* Instrumentation never interrupts the host. */
          }
        },
      );
      if (active()) engagementObserver = observer;
      else observer.close();
    } catch {
      /* Unavailable lifecycle APIs disable only page timing. */
    }
  }

  function startDevToolsObserver(): void {
    if (!extendedTelemetry) return;
    const observer = environment.devToolsObserver;
    if (!observer) return;
    try {
      const cleanup = observer((signal) => {
        if (!active() || devToolsEvents >= MAX_DEVTOOLS_EVENTS) return;
        const parsed = DevToolsSignalSchema.safeParse(signal);
        if (!parsed.success) return;
        devToolsEvents++;
        capture({ type: analyticsEventId.devToolsSuspected, payload: parsed.data });
      });
      if (typeof cleanup === "function") devToolsCleanup = cleanup;
    } catch {
      // A failed observer must not affect collection.
    }
  }

  function startErrorObserver(): void {
    if (!captureErrors || !errorEventsSupported || errorCleanup) return;
    const observer = environment.pageErrorObserver;
    if (!observer) return;
    try {
      errorCleanup = observer((report) => {
        if (!active()) return;
        const at = now();
        if (at - lastActivity >= idleSessionMs && !rotateSession(at)) return;
        if (errorEvents >= 20) return;
        const parsed = ErrorReportSchema.safeParse(report);
        if (!parsed.success) return;
        errorEvents++;
        capture({ type: analyticsEventId.errorReported, payload: parsed.data });
      });
    } catch {
      /* Best effort observer; normal site and collection continue. */
    }
  }

  function onPagehide(reason: "hidden" | "pagehide" = "pagehide"): void {
    if (!active() || status !== "ready") return;
    if (pagehideStarted) {
      if (pendingFinalSend !== "pagehide") pendingFinalSend = reason;
      return;
    }
    if (!handle || (queue.length === 0 && !departureSnapshot)) return;
    let supported = false;
    try {
      supported = environment.supportsKeepalive?.() === true;
    } catch {
      supported = false;
    }
    if (!supported) return;
    pagehideStarted = true;
    if (pumpTimer !== null) {
      clearTimeout(pumpTimer);
      pumpTimer = null;
    }
    // One bounded keepalive may overlap an already-started ordinary request on unload.
    // Immutable event IDs make the collector deduplicate that overlap atomically.
    void pagehideSend(reason)
      .catch(() => undefined)
      .finally(() => {
        pagehideStarted = false;
        if (pendingFinalSend) {
          const pending = pendingFinalSend;
          pendingFinalSend = null;
          onPagehide(pending);
        }
        if (!pumping) resolveFlushWaiters();
        if (reason === "pagehide" && !closed && status === "ready" && queue.length > 0)
          schedulePump(flushDebounceMs);
      });
  }

  const client: AnalyticsClient = {
    track(input) {
      try {
        track(input);
      } catch {
        report("internal_error");
      }
    },
    identify(patch) {
      try {
        identify(patch);
      } catch {
        report("internal_error");
      }
    },
    flush() {
      try {
        return requestFlush();
      } catch {
        return Promise.resolve();
      }
    },
    close() {
      try {
        closeClient();
      } catch {
        // Close is always safe to call.
      }
    },
  };

  if (!active()) return client;
  if (!environment.fetch) {
    disable("unsupported");
    return client;
  }
  if (!environment.storage) {
    disable("storage_unavailable");
    return client;
  }
  if (!environment.locks) {
    disable("lock_unavailable");
    return client;
  }
  if (!environment.randomUUID) {
    disable("crypto_unavailable");
    return client;
  }
  const hostname = readString(environment.hostname);
  if (!hostname) {
    disable("unsupported");
    return client;
  }
  if (options.config !== undefined) {
    const parsed = BrowserAnalyticsConfigSchema.safeParse(options.config);
    if (!parsed.success || parsed.data.hostname !== hostname) {
      disable("invalid_input");
      return client;
    }
    browserConfig = parsed.data;
  }
  scope = `browser:${hostname}`;
  if (!scopePattern.test(scope)) {
    disable("unsupported");
    return client;
  }
  const session = generateUuid();
  if (!session) {
    disable("crypto_unavailable");
    return client;
  }
  sessionId = session;
  lastActivity = now();
  if (environment.addPagehideListener) {
    pagehideListener = () => {
      if (!engagementObserver) {
        onPagehide();
        releaseSessionOwnership(true);
      }
    };
    try {
      environment.addPagehideListener(pagehideListener);
    } catch {
      pagehideListener = null;
    }
  }
  startDevToolsObserver();
  scheduleIdleTimer();
  void initialize().catch(() => {
    if (active()) disable("internal_error");
  });
  return client;
}
